/**
 * Cache Key Generation Utility
 *
 * Centralized cache key generation to ensure consistency between
 * cache storage (BookingDataProvider) and cache retrieval (useCalendarAvailability)
 */

/**
 * Generate cache key with advanced settings support
 * For preview mode (id === -1), includes all advanced settings in the key
 * to ensure cache invalidation when settings change
 *
 * CRITICAL: scheduleId is included to prevent cache collisions when switching schedules
 * CRITICAL: providerId is included to prevent cache collisions when switching providers
 *           in service-type booking flows. Without it, switching from Provider A to
 *           Provider B would serve stale cached data because the cache key was identical.
 */
export function generateCacheKey(
  meetingTypeId: number,
  year: number,
  month: number,
  timeZone: string,
  duration: number,
  advancedSettings?: {
    bufferTimeBefore?: number;
    bufferTimeAfter?: number;
    slotIncrementMinutes?: number;
    minimumBookingNotice?: number;
    maximumBookingWindow?: number;
  },
  scheduleId?: number | null,
  providerId?: number | null
): string {
  const base = `${meetingTypeId}-${year}-${month}-${timeZone}-${duration}`;

  // CRITICAL: Include scheduleId to prevent cache collisions when switching schedules
  // Use 'default' for null/undefined to differentiate from numbered schedules
  const scheduleKey = `schedule${scheduleId ?? 'default'}`;

  // CRITICAL: Include providerId to prevent cache collisions when switching providers
  // in service-type booking flows. undefined = not a service type (omit from key),
  // null = "No Preference" round-robin, number = specific provider
  const providerKey = providerId !== undefined
    ? `-provider${providerId ?? 'any'}`
    : '';

  // In preview mode (id === -1), include advanced settings in cache key
  // This ensures cache is invalidated when any advanced setting changes
  if (meetingTypeId === -1 && advancedSettings) {
    const bufferBefore = advancedSettings.bufferTimeBefore ?? 0;
    const bufferAfter = advancedSettings.bufferTimeAfter ?? 0;
    const slotIncrement = advancedSettings.slotIncrementMinutes ?? 30;
    const minNotice = advancedSettings.minimumBookingNotice ?? 0;
    const maxWindow = advancedSettings.maximumBookingWindow ?? 60;

    return `${base}-${bufferBefore}-${bufferAfter}-${slotIncrement}-${minNotice}-${maxWindow}-${scheduleKey}${providerKey}`;
  }

  return `${base}-${scheduleKey}${providerKey}`;
}
